import { deepmerge } from 'deepmerge-ts';

export type ResponseError = {
  status: number;
  body: unknown;
};

// Define a variable base URL. Server-side API requests have no idea how to resolve relative paths.
export const getPortalHost = () => {
  if (typeof window !== 'undefined') {
    return '';
  }

  return 'http://evo-api:8000';
};

export const getIngestionHost = () => {
  if (typeof window !== 'undefined') {
    return '';
  }

  return 'http://ingestion-api:8000';
};

const PORTAL_BASE_URL = getPortalHost() + '/api/evo/';
const INGESTION_BASE_URL = getIngestionHost() + '/api/ingestion/';

export const API = {
  // Infrastructure endpoints
  event_queue: PORTAL_BASE_URL + 'bioapi/customer/queue/',
  health: PORTAL_BASE_URL + 'bioapi/healthcheck/',

  // Referential endpoints
  countries: PORTAL_BASE_URL + 'bioapi/customer/countries/',
  country: PORTAL_BASE_URL + 'bioapi/customer/countries/@id/',
  common: PORTAL_BASE_URL + 'bioapi/common/',
  version: getPortalHost() + 'biologistex/common/version.json',

  // User Endpoints
  auth: PORTAL_BASE_URL + 'bioapi/customer/auth/login/',
  logout: PORTAL_BASE_URL + 'bioapi/customer/auth/logout/',
  currentUser: PORTAL_BASE_URL + 'bioapi/customer/auth/current/',
  resetPassword: PORTAL_BASE_URL + 'bioapi/customer/auth/resetpassword/',
  isUserLockedOut: PORTAL_BASE_URL + 'bioapi/customer/auth/isUserLockedOut/',

  // Owner Scoped Endpoints
  owner: PORTAL_BASE_URL + 'bioapi/customer/owner/',
  users: PORTAL_BASE_URL + 'bioapi/customer/owner/contacts/',
  user: PORTAL_BASE_URL + 'bioapi/customer/owner/contacts/@id/',
  publicShipments: PORTAL_BASE_URL + 'bioapi/customer/public_shipments/',
  publicShipment: PORTAL_BASE_URL + 'bioapi/customer/public_shipments/@id/',
  partnerClientShipments: PORTAL_BASE_URL + 'bioapi/client-partner/shipments/',
  shipments: PORTAL_BASE_URL + 'bioapi/customer/shipments/',
  shipment: PORTAL_BASE_URL + 'bioapi/customer/shipments/@id/',
  shipmentReport: PORTAL_BASE_URL + 'bioapi/customer/shipments/@id/report/',
  clients: PORTAL_BASE_URL + 'bioapi/customer/clients/',
  client: PORTAL_BASE_URL + 'bioapi/customer/clients/@id/',
  clientUsers: PORTAL_BASE_URL + 'bioapi/customer/clients/contacts/',
  partnerClients: PORTAL_BASE_URL + 'bioapi/courier-partner/clients/',
  partnerClient: PORTAL_BASE_URL + 'bioapi/courier-partner/clients/@id',
  evos: PORTAL_BASE_URL + 'bioapi/customer/evos/',
  evo: PORTAL_BASE_URL + 'bioapi/customer/evos/@id/',
  evoReadiness: PORTAL_BASE_URL + 'bioapi/customer/evos/upgradereadiness/',
  upgradeFirmware: PORTAL_BASE_URL + 'bioapi/customer/evos/upgradefirmware/@id/',
  cleaningRecords: PORTAL_BASE_URL + 'bioapi/customer/cleaning_records/',
  cleaningRecord: PORTAL_BASE_URL + 'bioapi/customer/cleaning_records/@id/',
  preparationRecords: PORTAL_BASE_URL + 'bioapi/customer/preparation_records/',
  preparationRecord: PORTAL_BASE_URL + 'bioapi/customer/preparation_records/@id/',
  evoPresets: PORTAL_BASE_URL + 'bioapi/customer/configtemplates/',
  evoPreset: PORTAL_BASE_URL + 'bioapi/customer/configtemplates/@id/',
  groups: PORTAL_BASE_URL + 'bioapi/customer/groups/',
  group: PORTAL_BASE_URL + 'bioapi/customer/groups/@id/',

  // Sendum endpoints
  locateNow: PORTAL_BASE_URL + 'bioapi/customer/views/locateNow/',
  geofences: PORTAL_BASE_URL + 'bioapi/customer/evos/@id/geofences/', // BLGX-2054

  // Administration endpoints
  cs_owners: PORTAL_BASE_URL + 'bioapi/csadmin/owners/',
  cs_owner: PORTAL_BASE_URL + 'bioapi/csadmin/owners/@id/',
  cs_bl_admins: PORTAL_BASE_URL + 'bioapi/csadmin/blAdmins/',
  cs_bl_admin: PORTAL_BASE_URL + 'bioapi/csadmin/blAdmins/@id/',
  cs_owners_users: PORTAL_BASE_URL + 'bioapi/csadmin/owners/users/',
  cs_owner_user: PORTAL_BASE_URL + 'bioapi/csadmin/owners/users/@id/',
  cs_owners_evos: PORTAL_BASE_URL + 'bioapi/csadmin/owners/evos/',
  cs_owner_evo: PORTAL_BASE_URL + 'bioapi/csadmin/owners/evos/@id/',
  unlock_account: PORTAL_BASE_URL + 'bioapi/csadmin/resetLoginAttempts',
  validate_owner_name: PORTAL_BASE_URL + 'bioapi/validation/ownerName/',
  validate_address: PORTAL_BASE_URL + 'bioapi/validation/address/',
  validate_user_email: PORTAL_BASE_URL + 'bioapi/validation/userEmail',
  vendors: PORTAL_BASE_URL + 'bioapi/csadmin/mobileEquipmentVendors/',

  // Ingestion API
  deviceList: INGESTION_BASE_URL + 'biologistex-ingestion/biologistex-ingestion/api/devices',
  deviceDetails: INGESTION_BASE_URL + 'biologistex-ingestion/api/views/device/detail',
  deviceMessages: INGESTION_BASE_URL + 'biologistex-ingestion/biologistex-ingestion/api/devices/messages',
  deviceAlarms: INGESTION_BASE_URL + 'biologistex-ingestion/biologistex-ingestion/api/devices/alarms',
  deviceBattery: INGESTION_BASE_URL + 'biologistex-ingestion/api/devices/battery',
  deviceLocation: INGESTION_BASE_URL + 'biologistex-ingestion/biologistex-ingestion/api/devices/location',
  ingestion_sensor_data: INGESTION_BASE_URL + 'biologistex-ingestion/api/views/shipment/detail',
  evoStatus: PORTAL_BASE_URL + 'bioapi/status/@id/',
  deviceReport: INGESTION_BASE_URL + 'biologistex-ingestion/api/views/device/report',
  provisionStatus: INGESTION_BASE_URL + 'biologistex-ingestion/biologistex/api/bioapi/status/@id',
  createProvision: INGESTION_BASE_URL + 'biologistex-ingestion/api/views/device/provision/create',

  // External endpoints
  googleTimezone: 'https://maps.googleapis.com/maps/api/timezone/json',

  // Evos endpoints
  evosV2: PORTAL_BASE_URL + 'bioapi/v2/evos/list/',
  evoV2: PORTAL_BASE_URL + 'bioapi/v2/evos/detail/@id/',

  // Shipment endpoints
  csvDataReport: PORTAL_BASE_URL + 'bioapi/v2/shipments/detail/@id/csvData',

  // New Shipment endpoints
  newShipmentMetaV2: PORTAL_BASE_URL + 'bioapi/v2/newShipment/meta/',
  newShipmentConfigTemplatesV2: PORTAL_BASE_URL + 'bioapi/v2/newShipment/configTemplates/',

  // Presets endpoints
  presetV2: PORTAL_BASE_URL + 'bioapi/v2/presets/detail/@id/',
  presetsV2: PORTAL_BASE_URL + 'bioapi/v2/presets/list',
  presetsModelTypesV2: PORTAL_BASE_URL + 'bioapi/v2/presets/modelTypes/',
  applicationConfigurationV2: PORTAL_BASE_URL + 'bioapi/v2/applicationConfiguration/',
} as const;

// --- API v2
// This implementation deprecates Ingestion batching, and simplifies usage.

/************************************************************************************************ 
  This library is intended to be paired with Tanstack Query, but can also be used directly.

  -- Query Usage: useQuery
  const owner = useQuery({
    queryKey: ['current-owner'],
    queryFn: () => apiClient('owner')
  });

  -- Query Usage: useMutation
  const newShipmentMutation = useMutation({
    mutationFn: (params) => await apiClient('shipments').post(params);
  });

  -- Direct Usage: GET with parameters
  const user = await apiClient('user', { id: '123' }).get();

  -- Direct Usage: POST with body data
  const newShipment = await apiClient('shipments').post({
    name: 'Test Shipment',
  });

  -- Specifying custom fetch options
  apiClient('shipment', { id: '456' }).get(
    undefined, 
    { redirect: 'error' }
  );
************************************************************************************************/

import { getSessionHeaders } from './cookies';

type EndpointKey = keyof typeof API;
type EndpointParams = Record<string, string | number>;
type RequestData = Record<string, any>; // eslint-disable-line

type APIClientMethod = (data?: RequestData, fetchOptions?: RequestInit) => Promise<unknown>;

type APIClient = {
  get: APIClientMethod;
  post: APIClientMethod;
  put: APIClientMethod;
  delete: APIClientMethod;
  patch: APIClientMethod;
};

// We need to attach cookies to API requests. This differs based on where the API is being rendered.
const getCookieHeaders = async () => {
  if (typeof window === 'undefined') {
    return {
      credentials: 'include',
      headers: {
        Cookie: (await getSessionHeaders()) as string,
      },
    };
  } else {
    const csrfToken =
      document.cookie
        .split(';')
        .find((cookie) => cookie.trim().startsWith('csrftoken='))
        ?.split('=')[1] || '';

    return {
      credentials: 'same-origin',
      headers: {
        Cookie: document.cookie,
        'X-CSRFToken': csrfToken,
      },
    };
  }
};

// Generate URL params or a stringified JSON body based on the HTTP verb.
const makeURL = (URL: string, method: string, data?: RequestData) => {
  let constructedURL = URL;
  const paramOptions: RequestInit = {};

  if (!data || !Object.keys(data).length) {
    return { constructedURL, paramOptions };
  }

  if (method === 'GET' || method === 'DELETE') {
    const queryString = new URLSearchParams(data).toString();
    constructedURL = `${constructedURL}?${queryString}`;
  } else {
    paramOptions.headers = {
      'Content-Type': 'application/json',
      ...paramOptions.headers,
    };

    paramOptions.body = JSON.stringify(data);
  }

  return { constructedURL, paramOptions };
};

// Generates a pre-configured fetch wrapper for a given HTTP verb.
const makeMethod = (URL: string, method: string): APIClientMethod => {
  return async (data?: RequestData, fetchOptions: RequestInit = {}) => {
    const { constructedURL, paramOptions } = makeURL(URL, method, data);

    const cookieOptions = await getCookieHeaders();

    const constructedFetchOptions = deepmerge(paramOptions, cookieOptions, fetchOptions, { method });

    const response = await fetch(constructedURL, constructedFetchOptions as RequestInit);

    let json;

    try {
      json = await response.json();
    } catch {
      json = null;
    }

    // When something goes wrong, window.Toast a default error message on 5xx errors.
    if (!response.ok) {
      if (typeof window !== 'undefined' && response.status.toString().startsWith('5')) {
        // @ts-expect-error -- We know that typescript is not explicitly aware of the Toast object.
        window?.Toast?.show && window.Toast.show('Something went wrong.', 5000); // eslint-disable-line -- This is intentionally an expression.
      }

      throw {
        status: response.status,
        body: json,
      } as ResponseError;
    }

    return json;
  };
};

export const apiClient = (endpoint: EndpointKey, params: EndpointParams = {}): APIClient => {
  let URL = API[endpoint];
  const requiredParams = URL.match(/@\w+/g)?.map((e) => e.replace('@', '')) || [];

  // Are all required parameters matched?
  for (const p of requiredParams) {
    if (!params.hasOwnProperty(p)) {
      throw new Error(`Missing required parameter: @${p} for endpoint: ${endpoint}`);
    }
  }

  // Were any unknown parameters provided?
  for (const p of Object.keys(params)) {
    if (requiredParams.indexOf(p) === -1) {
      throw new Error(`Unknown API parameter provided: ${p}`);
    }
  }

  // Construct our URL.
  Object.keys(params).forEach((p) => {
    URL = URL.replace(`@${p}`, `${params[p]}`);
  });

  return {
    get: makeMethod(URL, 'GET'),
    post: makeMethod(URL, 'POST'),
    put: makeMethod(URL, 'PUT'),
    delete: makeMethod(URL, 'DELETE'),
    patch: makeMethod(URL, 'PATCH'),
  };
};
